import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Card,
  Stack,
  Text,
  Title,
  Group,
  Badge,
  Loader,
  Alert,
  TextInput,
  Select,
  Button,
  NumberInput,
  SimpleGrid,
  Pagination,
  ActionIcon,
  Avatar,
  Modal
} from '@mantine/core';
import {
  IconSearch,
  IconExclamationCircle,
  IconRefresh,
  IconEye,
  IconShoppingCart,
  IconMessage,
  IconCrown,
  IconUser
} from '@tabler/icons-react';
import { api } from '../../services/api';
import { addToCart } from '../../store/slices/cartSlice';

const ProductBrowse = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useSelector(state => state.auth);
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sellerDetails, setSellerDetails] = useState(null);
  const [userSubscription, setUserSubscription] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  
  // Get seller filter from URL
  const sellerId = searchParams.get('seller');
  const sellerName = searchParams.get('seller_name');
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [condition, setCondition] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchProducts();
    if (sellerId) {
      fetchSellerDetails();
    }
    if (isAuthenticated) {
      fetchUserSubscription();
    }
  }, [currentPage, sellerId, isAuthenticated]);

  const fetchSellerDetails = async () => {
    try {
      const response = await api.get('/princesses/');
      const seller = response.sellers.find(s => s.id === parseInt(sellerId));
      if (seller) {
        setSellerDetails(seller);
      }
    } catch (err) {
      console.error('Error fetching seller details:', err);
    }
  };

  const fetchUserSubscription = async () => {
    try {
      const response = await api.getSubscription();
      if (response.plan) {
        setUserSubscription(response);
      }
    } catch (err) {
      console.error('Error fetching subscription:', err);
    }
  };

  const handleMessagePrincess = () => {
    if (!isAuthenticated) {
      window.location.href = '/';
      return;
    }
    
    // Staff members can always message princesses
    if (!user?.is_staff && (!userSubscription || userSubscription.plan !== 'paypiggiepro')) {
      setShowUpgradeModal(true);
      return;
    }
    
    // Navigate to conversation with the seller's user ID (not seller profile ID)
    if (sellerDetails?.user_id) {
      navigate(`/messages/${sellerDetails.user_id}`);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '12'
      });
      
      if (searchTerm) params.append('search', searchTerm);
      if (condition) params.append('condition', condition);
      if (minPrice) params.append('min_price', minPrice);
      if (maxPrice) params.append('max_price', maxPrice);
      if (sellerId) params.append('seller_id', sellerId);

      const response = await api.getProducts(Object.fromEntries(params));
      setProducts(response.products);
      setTotalPages(response.pagination.pages);
    } catch (err) {
      console.error('Error fetching products:', err);
      setError(err.response?.data?.error || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchProducts();
  };

  const handleAddToCart = (product) => {
    dispatch(addToCart({ product, quantity: 1 }));
  };

  const getConditionColor = (condition) => {
    switch (condition) {
      case 'new': return 'green';
      case 'like_new': return 'blue';
      case 'used_excellent': return 'teal';
      case 'used_good': return 'yellow';
      case 'used_fair': return 'orange';
      case 'refurbished': return 'grape';
      default: return 'gray';
    }
  };

  const formatCondition = (condition) => {
    return condition.replace('_', ' ').split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const conditionOptions = [
    { value: '', label: 'All Conditions' },
    { value: 'new', label: 'New' },
    { value: 'like_new', label: 'Like New' },
    { value: 'used_excellent', label: 'Used - Excellent' },
    { value: 'used_good', label: 'Used - Good' },
    { value: 'used_fair', label: 'Used - Fair' },
    { value: 'refurbished', label: 'Refurbished' }
  ];

  if (loading && products.length === 0) {
    return (
      <Container size="lg" py="xl">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
          <Loader size="lg" />
        </div>
      </Container>
    );
  }

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        {/* Princess Profile Header */}
        {sellerId && sellerDetails && (
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Stack gap="md">
              <Group align="flex-start">
                <Avatar
                  src={sellerDetails.primary_profile_image}
                  size="xl"
                  radius="md"
                  color="blue"
                >
                  <IconUser size="2rem" />
                </Avatar>
                <div style={{ flex: 1 }}>
                  <Group gap="sm" mb="xs">
                    <Title order={2}>{sellerDetails.business_display_name}</Title>
                    {sellerDetails.is_verified && (
                      <Badge color="green" variant="light" size="md">
                        Verified Princess
                      </Badge>
                    )}
                  </Group>
                  <Text size="md" c="dimmed" mb="sm">
                    {sellerDetails.business_description || 'Welcome to my store!'}
                  </Text>
                  <Group gap="xs">
                    <Text size="sm" c="dimmed">
                      {sellerDetails.business_city}, {sellerDetails.business_state}
                    </Text>
                    <Text size="sm" c="dimmed">•</Text>
                    <Text size="sm" c="dimmed">
                      {sellerDetails.total_orders} orders • {sellerDetails.total_reviews} reviews
                    </Text>
                  </Group>
                </div>
              </Group>
              
              {/* Message Button */}
              <Button
                leftSection={<IconMessage size="1rem" />}
                onClick={handleMessagePrincess}
                variant="gradient"
                gradient={{ from: 'blue', to: 'grape' }}
              >
                Message the Princess
              </Button>
            </Stack>
          </Card>
        )}

        {/* Header */}
        <div>
          <Title order={1}>{sellerId ? `${sellerName}'s Products` : 'Browse Products'}</Title>
          {sellerId && (
            <Group mt="sm">
              <Button
                variant="subtle"
                size="xs"
                onClick={() => window.location.href = '/products'}
              >
                ← Back to all products
              </Button>
            </Group>
          )}
        </div>

        {/* Search and Filters */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Stack gap="md">
            <Group align="end" gap="md">
              <TextInput
                label="Search"
                placeholder="Search products..."
                leftSection={<IconSearch size="0.9rem" />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.currentTarget.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                style={{ flex: 1 }}
              />
              <Select
                label="Condition"
                placeholder="Any condition"
                value={condition}
                onChange={setCondition}
                data={conditionOptions}
                style={{ minWidth: 150 }}
              />
              <NumberInput
                label="Min Price"
                placeholder="$0"
                value={minPrice}
                onChange={setMinPrice}
                min={0}
                step={1}
                style={{ minWidth: 120 }}
              />
              <NumberInput
                label="Max Price"
                placeholder="$1000"
                value={maxPrice}
                onChange={setMaxPrice}
                min={0}
                step={1}
                style={{ minWidth: 120 }}
              />
              <Button onClick={handleSearch} loading={loading}>
                Search
              </Button>
              <ActionIcon onClick={fetchProducts} variant="light">
                <IconRefresh size="1rem" />
              </ActionIcon>
            </Group>

            {error && (
              <Alert icon={<IconExclamationCircle size="1rem" />} color="red">
                {error}
              </Alert>
            )}
          </Stack>
        </Card>

        {/* Products Grid */}
        {products.length === 0 && !loading ? (
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <Text c="dimmed" size="lg">No products found</Text>
              <Text c="dimmed" mt="xs">Try adjusting your search criteria</Text>
            </div>
          </Card>
        ) : (
          <SimpleGrid
            cols={{ base: 1, sm: 2, md: 3, lg: 4 }}
            spacing="md"
          >
            {products.map((product) => (
              <Card 
                key={product.id} 
                shadow="sm" 
                padding="lg" 
                radius="md" 
                withBorder
                style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
              >
                <Stack gap="sm" style={{ flex: 1 }}>
                  {/* Product Image Placeholder */}
                  <div
                    style={{
                      height: 200,
                      backgroundColor: '#f8f9fa',
                      borderRadius: 8,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: 8
                    }}
                  >
                    {product.primary_image ? (
                      <img
                        src={product.primary_image}
                        alt={product.title}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'cover',
                          borderRadius: 8
                        }}
                      />
                    ) : (
                      <Text c="dimmed" size="sm">No Image</Text>
                    )}
                  </div>

                  {/* Product Info */}
                  <div style={{ flex: 1 }}>
                    <Title 
                      order={4} 
                      lineClamp={2} 
                      mb="xs"
                      component={Link}
                      to={`/products/${product.id}`}
                      style={{ 
                        textDecoration: 'none', 
                        color: 'inherit',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => e.target.style.color = 'var(--mantine-color-blue-6)'}
                      onMouseLeave={(e) => e.target.style.color = 'inherit'}
                    >
                      {product.title}
                    </Title>

                    <Group gap="xs" mb="sm">
                      <Badge color={getConditionColor(product.condition)} variant="light" size="sm">
                        {formatCondition(product.condition)}
                      </Badge>
                      {product.quantity === 0 && (
                        <Badge color="red" variant="light" size="sm">
                          Out of Stock
                        </Badge>
                      )}
                    </Group>

                    <Text size="lg" fw={700} mb="xs">
                      ${product.price}
                    </Text>

                    <Text size="sm" c="dimmed" mb="xs">
                      By {product.seller.business_display_name}
                    </Text>

                    <Group gap="xs">
                      <Text size="xs" c="dimmed">
                        {product.views} views
                      </Text>
                      {product.seller.is_verified && (
                        <Badge color="blue" variant="light" size="xs">
                          Verified Princess
                        </Badge>
                      )}
                    </Group>
                  </div>

                  {/* Action Buttons */}
                  <Group gap="xs">
                    <Button
                      component={Link}
                      to={`/products/${product.id}`}
                      variant="light"
                      leftSection={<IconEye size="1rem" />}
                      disabled={!product.is_available}
                      style={{ flex: 1 }}
                    >
                      View Details
                    </Button>
                    <Button
                      variant="filled"
                      size="sm"
                      onClick={(e) => {
                        e.preventDefault();
                        handleAddToCart(product);
                      }}
                      disabled={!product.is_available || product.quantity === 0}
                      leftSection={<IconShoppingCart size="0.9rem" />}
                      style={{ flex: 1 }}
                    >
                      Add to Cart
                    </Button>
                  </Group>
                </Stack>
              </Card>
            ))}
          </SimpleGrid>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <Group align="center">
            <Pagination
              value={currentPage}
              onChange={setCurrentPage}
              total={totalPages}
            />
          </Group>
        )}
      </Stack>

      {/* Upgrade Modal */}
      <Modal
        opened={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        title="Upgrade to PayPiggiePro"
        centered
      >
        <Stack gap="md">
          <Group gap="sm">
            <IconCrown size="2rem" color="var(--mantine-color-yellow-6)" />
            <div>
              <Text fw={500}>Premium Feature</Text>
              <Text size="sm" c="dimmed">
                Messaging princesses is only available for PayPiggiePro subscribers
              </Text>
            </div>
          </Group>
          <Alert color="blue" variant="light">
            <Text size="sm">
              Upgrade to PayPiggiePro to unlock:
            </Text>
            <ul style={{ marginTop: '0.5rem', marginBottom: 0 }}>
              <li>Direct messaging with all princesses</li>
              <li>Priority customer support</li>
              <li>Exclusive deals and discounts</li>
              <li>Early access to new products</li>
            </ul>
          </Alert>
          <Group justify="flex-end" gap="sm">
            <Button
              variant="light"
              onClick={() => setShowUpgradeModal(false)}
            >
              Maybe Later
            </Button>
            <Button
              variant="gradient"
              gradient={{ from: 'blue', to: 'grape' }}
              leftSection={<IconCrown size="1rem" />}
              onClick={() => window.location.href = '/subscription'}
            >
              Upgrade Now - $25/month
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
};

export default ProductBrowse;